/** SUNTRONIX ONE — canonical technology lifecycle model.
 *  This is the single source of truth used across login, homepage, insights,
 *  Asset 360, service catalogue and any surface that renders the full lifecycle.
 *
 *  IMPORTANT: The seven-stage ordering below is a product standard.  Do NOT
 *  re-order these locally; import them from here instead.  Partial lifecycles
 *  (e.g. "Procure · Deploy" on a marketing tile) may show a slice, but any
 *  place that intends the FULL lifecycle must render all seven stages in this
 *  order.
 */

/** Present-tense verbs (canonical labels used in taglines, service catalogue,
 *  admin navigation and lifecycle rails). */
export const LIFECYCLE_STAGES = [
  {
    key: "procure",
    label: "Procure",
    past: "Procured",
    verb: "Procure",
    tagline: "Technology selected for the right requirement.",
    summary: "Requirement, sourcing, quotation, purchase, receiving, asset creation.",
  },
  {
    key: "deploy",
    label: "Deploy",
    past: "Deployed",
    verb: "Deploy",
    tagline: "Ready for the employee.",
    summary: "Prepare, configure, enrol, allocate, pack, ship, deliver, assign.",
  },
  {
    key: "manage",
    label: "Manage",
    past: "Managed",
    verb: "Manage",
    tagline: "Every technology asset. One trusted record.",
    summary: "Golden Asset Record — assignment, location, software, contract, warranty.",
  },
  {
    key: "support",
    label: "Support",
    past: "Supported",
    verb: "Support",
    tagline: "Support with full lifecycle context.",
    summary: "Service, repair, warranty, replacement, swap, loaner, health.",
  },
  {
    key: "recover",
    label: "Recover",
    past: "Recovered",
    verb: "Recover",
    tagline: "Every asset accounted for.",
    summary: "Retrieve technology from employees, sites or projects into controlled custody.",
  },
  {
    key: "reuse",
    label: "Reuse",
    past: "Reused",
    verb: "Reuse",
    tagline: "Extend value before replacing.",
    summary: "Inspect, grade, repair, refurbish, redeploy, reallocate, resale/buyback.",
  },
  {
    key: "recycle",
    label: "Recycle",
    past: "Recycled",
    verb: "Recycle",
    tagline: "Close the lifecycle responsibly.",
    summary: "Approved ITAD, e-waste recycling, material recovery, certified destruction, evidence.",
  },
];

/** Short-form labels — used where the full label ladder must be printed. */
export const LIFECYCLE_LABELS = LIFECYCLE_STAGES.map((s) => s.label);
export const LIFECYCLE_PAST_LABELS = LIFECYCLE_STAGES.map((s) => s.past);

/** Uppercase brand signature — used in marketing surfaces, login tagline and QBR decks. */
export const LIFECYCLE_SIGNATURE = LIFECYCLE_LABELS.map((s) => s.toUpperCase()).join(" · ");

/** Final-disposition outcomes (see Section 13 of the lifecycle spec).
 *  These sit BENEATH the lifecycle stages RECOVER / REUSE / RECYCLE and must
 *  not be confused with the lifecycle itself.  RECYCLED is one outcome among
 *  many; do not force it when the actual outcome differs. */
export const FINAL_DISPOSITIONS = [
  { key: "redeployed",   label: "Redeployed",       under: "reuse"   },
  { key: "reused",       label: "Reused",           under: "reuse"   },
  { key: "refurbished",  label: "Refurbished",      under: "reuse"   },
  { key: "resold",       label: "Resold",           under: "reuse"   },
  { key: "buyback",      label: "Buyback",          under: "reuse"   },
  { key: "donated",      label: "Donated",          under: "reuse"   },
  { key: "recycled",     label: "Recycled",         under: "recycle" },
  { key: "destroyed",    label: "Destroyed",        under: "recycle" },
  { key: "returned_oem", label: "Returned to OEM",  under: "recycle" },
  { key: "other",        label: "Other Approved",   under: "recycle" },
];

/** Optional presentation strings used by the brand tagline. */
export const LIFECYCLE_BRAND_LINE = "One platform. Every technology asset. Every stage.";
export const LIFECYCLE_LONG_LINE =
  "From technology requirement to responsible end-of-life, ONE keeps every stage connected.";
